import { useState } from 'react'
import type { PhotoStore, PinVault } from '@application/ports.ts'
import type { Adaptations, Moment, Step } from '@domain/events.ts'
import type { Reminder } from '@domain/rules/pending.ts'
import type { FamilyState, Member } from '@domain/state.ts'
import { childrenOf, tasksOf } from '@domain/state.ts'
import type { MemberId, PhotoId, TaskId } from '@domain/ids.ts'
import type { ChildSetup } from '@domain/seed.ts'
import { es } from '../strings/es.ts'
import { ChildPinSetting } from './ChildPinSetting.tsx'
import { LookSetting } from './LookSetting.tsx'
import { JuegoSetting } from './JuegoSetting.tsx'
import { GenteSetting } from './GenteSetting.tsx'
import { QuitarMiembro } from './QuitarMiembro.tsx'
import { RemindersSetting } from './RemindersSetting.tsx'
import { NewTask } from './NewTask.tsx'
import { TaskEditor } from './TaskEditor.tsx'

function Toggle({
  id,
  label,
  help,
  checked,
  onChange,
}: {
  id: string
  label: string
  help?: string
  checked: boolean
  onChange: (value: boolean) => void
}) {
  return (
    <div className="mb-4 flex items-start gap-3">
      <input
        id={id}
        type="checkbox"
        className="mt-3 h-6 w-6 min-h-0 shrink-0"
        checked={checked}
        onChange={(e) => {
          onChange(e.target.checked)
        }}
      />
      <label htmlFor={id}>
        <span className="block font-bold">{label}</span>
        {help ? <span className="text-muted block text-sm">{help}</span> : null}
      </label>
    </div>
  )
}

/**
 * Per-child settings, and the photograph of what "done" looks like.
 *
 * The photo is the important half. A child who insists he did his job has
 * usually understood the task his own way, in good faith; a picture taken once
 * replaces an argument with something both people can point at.
 */
export function ChildConfig({
  state,
  photos,
  vault,
  onPinChanged,
  onAdaptations,
  onSetPhoto,
  onRename,
  onSteps,
  onArchive,
  onRestore,
  onCreateTask,
  onRetune,
  onLook,
  onArchiveMember,
  onRestoreMember,
  onAddAdult,
  onAddChild,
  onReminders,
  onBack,
}: {
  state: FamilyState
  photos: PhotoStore | null
  vault: PinVault | null
  onPinChanged: () => void
  onAdaptations: (memberId: MemberId, adaptations: Adaptations) => void
  onSetPhoto: (taskId: TaskId, photoId: PhotoId) => void
  onRename: (taskId: TaskId, name: string, emoji: string) => void
  onSteps: (taskId: TaskId, steps: readonly Step[]) => void
  onArchive: (taskId: TaskId, reason: string) => void
  onRestore: (taskId: TaskId) => void
  onCreateTask: (
    memberId: MemberId,
    task: {
      kind: 'routine' | 'mission'
      moment: Moment | undefined
      name: string
      steps: readonly string[]
      coins: number
      /** Qué juego la da por hecha, si la da por hecha un juego. */
      game?: 'mates'
    },
  ) => Promise<string | null>
  onRetune: (taskId: TaskId, coins: number, xp: number) => void
  onLook: (memberId: MemberId, accent: string, emoji: string, coinWord: string) => void
  onArchiveMember: (memberId: MemberId, reason: string) => void
  onRestoreMember: (memberId: MemberId) => void
  onAddAdult: (name: string) => void
  onAddChild: (child: Omit<ChildSetup, 'id'>) => void
  onReminders: (memberId: MemberId, avisos: readonly Reminder[]) => void
  onBack: () => void
}) {
  const children = childrenOf(state)
  // Los que están fuera de las listas, para poder devolverlos. Salen los hijos y
  // también los adultos: si se quita a uno por error, tiene que haber vuelta.
  const quitados = [...state.members.values()].filter((m) => m.archived)
  const [open, setOpen] = useState<MemberId | null>(children[0]?.id ?? null)

  const patch = (member: Member, change: Partial<Adaptations>): void => {
    onAdaptations(member.id, { ...member.adaptations, ...change })
  }

  return (
    <div className="mx-auto max-w-2xl px-5 pt-5 pb-16">
      <button type="button" onClick={onBack} className="text-muted mb-5 text-sm font-bold">
        {es.today.back}
      </button>
      <h1 className="mb-6 text-2xl font-extrabold tracking-tight">{es.config.title}</h1>

      {children.map((child) => (
        <section key={child.id} className="mb-8">
          <button
            type="button"
            className="mb-4 text-xl font-extrabold"
            style={{ color: child.accent }}
            aria-expanded={open === child.id}
            onClick={() => {
              setOpen(open === child.id ? null : child.id)
            }}
          >
            {child.emoji} {child.name}
          </button>

          {open === child.id ? (
            <>
              <Toggle
                id={`one-${child.id}`}
                label={es.config.oneThing}
                help={es.config.oneThingHelp}
                checked={child.adaptations.oneThingAtATime ?? false}
                onChange={(value) => {
                  patch(child, { oneThingAtATime: value })
                }}
              />
              <Toggle
                id={`lit-${child.id}`}
                label={es.config.literal}
                checked={child.adaptations.literalLanguage}
                onChange={(value) => {
                  patch(child, { literalLanguage: value, noSurprises: value })
                }}
              />
              <Toggle
                id={`big-${child.id}`}
                label={es.config.largeText}
                checked={child.adaptations.largeText}
                onChange={(value) => {
                  patch(child, { largeText: value })
                }}
              />
              <Toggle
                id={`mot-${child.id}`}
                label={es.config.reducedMotion}
                checked={child.adaptations.reducedMotion}
                onChange={(value) => {
                  patch(child, { reducedMotion: value })
                }}
              />

              <RemindersSetting
                member={child}
                onSave={(avisos) => {
                  onReminders(child.id, avisos)
                }}
              />

              <LookSetting
                member={child}
                onSave={(accent, emoji, coinWord) => {
                  onLook(child.id, accent, emoji, coinWord)
                }}
              />

              {vault ? (
                <ChildPinSetting member={child} vault={vault} onChanged={onPinChanged} />
              ) : null}

              <JuegoSetting
                member={child}
                mision={tasksOf(state, child.id, 'mission').find((t) => t.game === 'mates')}
                onCreate={(coins) => {
                  void onCreateTask(child.id, {
                    kind: 'mission',
                    moment: undefined,
                    name: es.config.juego,
                    steps: [],
                    coins,
                    game: 'mates',
                  })
                }}
                onRemove={(taskId) => {
                  onArchive(taskId, es.config.juegoRemoveReason)
                }}
              />

              <h3 className="text-muted mt-8 mb-1 text-xs font-bold tracking-[0.1em] uppercase">
                {es.config.tasks}
              </h3>
              <p className="text-muted mt-0 mb-3 text-sm">{es.config.photosHelp}</p>
              <ul className="grid list-none gap-3 p-0">
                {tasksOf(state, child.id).map((task) => (
                  <TaskEditor
                    key={task.id}
                    task={task}
                    photos={photos}
                    onRename={onRename}
                    onSteps={onSteps}
                    onSetPhoto={onSetPhoto}
                    onArchive={onArchive}
                    onRetune={onRetune}
                  />
                ))}
              </ul>
              <NewTask onCreate={(task) => onCreateTask(child.id, task)} />

              <QuitarMiembro member={child} onArchive={onArchiveMember} />

              {[...state.tasks.values()].filter((t) => t.memberId === child.id && t.archived)
                .length > 0 ? (
                <>
                  <h3 className="text-muted mt-8 mb-2 text-xs font-bold tracking-[0.1em] uppercase">
                    {es.config.archived}
                  </h3>
                  <ul className="grid list-none gap-2 p-0">
                    {[...state.tasks.values()]
                      .filter((t) => t.memberId === child.id && t.archived)
                      .map((task) => (
                        <li
                          key={task.id}
                          className="border-rule m-0 flex items-center gap-3 rounded-xl border p-3"
                        >
                          <span className="text-muted min-w-0 flex-1">
                            {task.emoji} {task.name}
                          </span>
                          <button
                            type="button"
                            className="border-rule shrink-0 rounded-lg border px-4 text-sm font-bold"
                            onClick={() => {
                              onRestore(task.id)
                            }}
                          >
                            {es.config.restore}
                          </button>
                        </li>
                      ))}
                  </ul>
                </>
              ) : null}
            </>
          ) : null}
        </section>
      ))}

      <GenteSetting
        state={state}
        onAddAdult={onAddAdult}
        onAddChild={onAddChild}
        onArchiveMember={onArchiveMember}
      />

      {quitados.length > 0 ? (
        <section className="border-rule mt-4 border-t pt-6">
          <h2 className="text-muted mt-0 mb-3 text-xs font-bold tracking-[0.1em] uppercase">
            {es.config.quitados}
          </h2>
          <ul className="grid list-none gap-2 p-0">
            {quitados.map((member) => (
              <li
                key={member.id}
                className="border-rule m-0 flex items-center gap-3 rounded-xl border p-3"
              >
                <span className="text-muted min-w-0 flex-1">
                  {member.emoji} {member.name}
                </span>
                <button
                  type="button"
                  className="border-rule shrink-0 rounded-lg border px-4 text-sm font-bold"
                  onClick={() => {
                    onRestoreMember(member.id)
                  }}
                >
                  {es.config.volverAPoner(member.name)}
                </button>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  )
}
